import { useState } from 'react';
import { Button } from '../../../components/Button';
import { PageLayout } from '../../../components/PageLayout';
import { PlusCircleIcon } from '@heroicons/react/16/solid';
import { BookType } from '../api/types';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { useIsRoute } from '../../../hooks/useIsRoute';
import clsx from 'clsx';
import { useEditModal } from '../hooks/useEditModal';
import { useDeleteModal } from '../hooks/useDeleteModal';
import { useCreateBook } from '../hooks/useCreateBook';
import { DraftBooks } from '../components/DraftBooks';
import { ActiveBooks } from '../components/ActiveBooks';

export const MyBooks = () => {
  const isDraftPage = useIsRoute('/my-books/draft');
  const isActivePage = useIsRoute('/my-books');

  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [bookToDelete, setBookToDelete] = useState<BookType | null>(null);
  const [bookToEdit, setBookToEdit] = useState<BookType | null>(null);

  const onCloseModal = () => {
    if (isDeleteModalOpen) {
      setIsDeleteModalOpen(false);
    }
    if (isOpen) {
      setIsOpen(false);
    }
    if (bookToDelete) {
      setBookToDelete(null);
    }
    if (bookToEdit) {
      setBookToEdit(null);
    }
  };

  const { CreateBookModal } = useCreateBook({ isOpen, onCloseModal });
  const { EditModal } = useEditModal({ bookToEdit, onCloseModal });
  const { DeleteModal } = useDeleteModal({ bookToDelete, onCloseModal });

  const onEditPress = (book: BookType) => {
    setBookToEdit(book);
  };

  const onDeletePress = (book: BookType) => {
    setBookToDelete(book);
    setIsDeleteModalOpen(true);
  };

  return (
    <PageLayout
      title="My books"
      buttonRight={
        <Button onClick={() => setIsOpen(true)}>
          <PlusCircleIcon className="size-5 fill-white" />
          Add new book
        </Button>
      }>
      <TabGroup selectedIndex={isActivePage ? 0 : 1}>
        <TabList className="flex gap-1">
          <Link to="/my-books">
            <Tab
              className={clsx(
                'rounded-full py-1 px-3 text-sm font-semibold text-cyan-800 focus:outline-none hover:bg-cyan-400 transition duration-200',
                isActivePage && 'bg-cyan-800 text-white'
              )}>
              Active
            </Tab>
          </Link>
          <Link to="/my-books/draft">
            <Tab
              className={clsx(
                'rounded-full py-1 px-3 text-sm font-semibold text-cyan-800 focus:outline-none hover:bg-cyan-400 transition duration-200',
                isDraftPage && 'bg-cyan-800 text-white'
              )}>
              Draft
            </Tab>
          </Link>
        </TabList>
        <TabPanels className="mt-3">
          <TabPanel className="rounded-xl bg-white/5 flex flex-col gap-3">
            <ActiveBooks
              isActivePage={Boolean(isActivePage)}
              onDeletePress={onDeletePress}
              onEditPress={onEditPress}
            />
          </TabPanel>
          <TabPanel className="rounded-xl bg-white/5 flex flex-col gap-3">
            <DraftBooks
              isDraftPage={Boolean(isDraftPage)}
              onDeletePress={onDeletePress}
              onEditPress={onEditPress}
            />
          </TabPanel>
        </TabPanels>
      </TabGroup>

      {CreateBookModal}
      {DeleteModal}
      {EditModal}
    </PageLayout>
  );
};
